import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Star,
  Plus,
  Edit,
  Trash2,
  User,
  Building,
  Calendar,
  MessageSquare,
  TrendingUp,
  TrendingDown,
  Award,
  Target,
  Zap,
  Users,
  Clock,
  CheckCircle
} from "lucide-react";
import axios from "../../axios/AxiosInstance";
import { toast } from "react-toastify";

const RatingsSection = ({ candidateId }) => {
  const [ratings, setRatings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  useEffect(() => {
    loadRatings();
  }, [candidateId]);

  const loadRatings = async () => {
    try {
      setIsLoading(true);
      // Simuler des données de notations pour l'instant
      // TODO: Remplacer par l'API réelle
      const mockRatings = [
        {
          id: 1,
          rater: "Sophie Martin",
          raterRole: "Chef de Projet",
          client: "TechCorp Solutions",
          mission: "Développeur Full Stack",
          date: "2024-01-15",
          overallRating: 4.5,
          criteria: {
            technical: 5,
            communication: 4,
            punctuality: 5,
            teamwork: 4,
            problemSolving: 5,
            adaptability: 4
          },
          comments: "Excellent développeur, très autonome et proactif. Qualité de code remarquable.",
          strengths: ["Expertise technique", "Autonomie", "Qualité du code"],
          improvements: ["Communication avec l'équipe business"]
        },
        {
          id: 2,
          rater: "Marc Dubois",
          raterRole: "Directeur Technique",
          client: "Digital Innovations",
          mission: "Chef de Projet IT",
          date: "2024-01-08",
          overallRating: 4.8,
          criteria: {
            technical: 5,
            communication: 5,
            punctuality: 5,
            teamwork: 5,
            problemSolving: 4,
            adaptability: 5
          },
          comments: "Leadership exceptionnel, a su mener l'équipe vers le succès du projet.",
          strengths: ["Leadership", "Gestion d'équipe", "Vision stratégique"],
          improvements: ["Gestion du stress en fin de projet"]
        },
        {
          id: 3,
          rater: "Claire Rousseau",
          raterRole: "Responsable RH",
          client: "Enterprise Systems",
          mission: "Consultant SAP",
          date: "2023-12-20",
          overallRating: 4.2,
          criteria: {
            technical: 4,
            communication: 4,
            punctuality: 5,
            teamwork: 4,
            problemSolving: 4,
            adaptability: 4
          },
          comments: "Bonne expertise SAP, formation des utilisateurs efficace.",
          strengths: ["Expertise SAP", "Formation", "Ponctualité"],
          improvements: ["Adaptation aux processus métier", "Documentation"]
        }
      ];
      
      setTimeout(() => {
        setRatings(mockRatings);
        setIsLoading(false);
      }, 500);
    } catch (error) {
      console.error("Erreur lors du chargement des notations:", error);
      toast.error("Erreur lors du chargement des notations");
      setIsLoading(false);
    }
  };

  const calculateAverageRating = () => {
    if (ratings.length === 0) return 0;
    const sum = ratings.reduce((acc, rating) => acc + rating.overallRating, 0);
    return (sum / ratings.length).toFixed(1);
  };

  const calculateCriteriaAverages = () => {
    if (ratings.length === 0) return {};
    
    const criteriaKeys = Object.keys(ratings[0].criteria);
    const averages = {};
    
    criteriaKeys.forEach(criteria => {
      const sum = ratings.reduce((acc, rating) => acc + rating.criteria[criteria], 0);
      averages[criteria] = (sum / ratings.length).toFixed(1);
    });
    
    return averages;
  };

  const renderStars = (rating, size = "w-4 h-4") => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center space-x-1">
        {[...Array(fullStars)].map((_, i) => (
          <Star key={`full-${i}`} className={`${size} text-yellow-400 fill-current`} />
        ))}
        {hasHalfStar && (
          <div className="relative">
            <Star className={`${size} text-gray-300`} />
            <div className="absolute inset-0 overflow-hidden w-1/2">
              <Star className={`${size} text-yellow-400 fill-current`} />
            </div>
          </div>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <Star key={`empty-${i}`} className={`${size} text-gray-300`} />
        ))}
        <span className="ml-2 text-sm font-medium text-gray-700">{rating}</span>
      </div>
    );
  };

  const getCriteriaIcon = (criteria) => {
    switch (criteria) {
      case "technical":
        return <Zap className="w-4 h-4" />;
      case "communication":
        return <MessageSquare className="w-4 h-4" />;
      case "punctuality":
        return <Clock className="w-4 h-4" />;
      case "teamwork":
        return <Users className="w-4 h-4" />;
      case "problemSolving":
        return <Target className="w-4 h-4" />;
      case "adaptability":
        return <TrendingUp className="w-4 h-4" />;
      default:
        return <CheckCircle className="w-4 h-4" />;
    }
  };

  const getCriteriaLabel = (criteria) => {
    const labels = {
      technical: "Compétences techniques",
      communication: "Communication",
      punctuality: "Ponctualité",
      teamwork: "Travail d'équipe",
      problemSolving: "Résolution de problèmes",
      adaptability: "Adaptabilité"
    };
    return labels[criteria] || criteria;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR");
  };

  const averageRating = calculateAverageRating();
  const criteriaAverages = calculateCriteriaAverages();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec bouton d'ajout */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Évaluations & Notations</h2>
          <p className="text-gray-600 mt-1">
            Retours et évaluations des clients et managers
          </p>
        </div>
        <motion.button
          onClick={() => setIsAddModalOpen(true)}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus className="w-4 h-4" />
          <span>Nouvelle évaluation</span>
        </motion.button>
      </div>

      {/* Vue d'ensemble des notations */}
      {ratings.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Note globale */}
          <motion.div
            className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Award className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Note Globale</h3>
                <p className="text-sm text-gray-600">Moyenne sur {ratings.length} évaluations</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-4xl font-bold text-yellow-600">{averageRating}</div>
              <div>{renderStars(parseFloat(averageRating), "w-6 h-6")}</div>
            </div>
          </motion.div>

          {/* Détail par critères */}
          <motion.div
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Détail par Critères</h3>
            <div className="space-y-3">
              {Object.entries(criteriaAverages).map(([criteria, average]) => (
                <div key={criteria} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="text-gray-400">
                      {getCriteriaIcon(criteria)}
                    </div>
                    <span className="text-sm text-gray-700">{getCriteriaLabel(criteria)}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {renderStars(parseFloat(average))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      )}

      {/* Liste des évaluations */}
      {ratings.length === 0 ? (
        <motion.div
          className="text-center py-12 bg-gray-50 rounded-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Star className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Aucune évaluation
          </h3>
          <p className="text-gray-600 mb-4">
            Les évaluations des clients et managers apparaîtront ici
          </p>
          <motion.button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            whileHover={{ scale: 1.02 }}
          >
            Ajouter une évaluation
          </motion.button>
        </motion.div>
      ) : (
        <div className="space-y-6">
          {ratings.map((rating, index) => (
            <motion.div
              key={rating.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {rating.rater}
                    </h3>
                    <p className="text-gray-600">{rating.raterRole}</p>
                    <p className="text-sm text-gray-500 flex items-center space-x-1">
                      <Building className="w-4 h-4" />
                      <span>{rating.client} - {rating.mission}</span>
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className="flex items-center space-x-2">
                      {renderStars(rating.overallRating)}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      {formatDate(rating.date)}
                    </p>
                  </div>
                  <div className="flex space-x-1">
                    <motion.button
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      whileHover={{ scale: 1.1 }}
                    >
                      <Edit className="w-4 h-4" />
                    </motion.button>
                    <motion.button
                      onClick={() => {
                        if (window.confirm("Êtes-vous sûr de vouloir supprimer cette évaluation ?")) {
                          setRatings(ratings.filter(r => r.id !== rating.id));
                          toast.success("Évaluation supprimée");
                        }
                      }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      whileHover={{ scale: 1.1 }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </motion.button>
                  </div>
                </div>
              </div>

              {/* Critères détaillés */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                {Object.entries(rating.criteria).map(([criteria, score]) => (
                  <div key={criteria} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="text-gray-400">
                        {getCriteriaIcon(criteria)}
                      </div>
                      <span className="text-sm text-gray-700">{getCriteriaLabel(criteria)}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      {renderStars(score)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Commentaires */}
              {rating.comments && (
                <div className="mb-4">
                  <h4 className="font-medium text-gray-900 mb-2">Commentaires</h4>
                  <p className="text-gray-700 bg-gray-50 rounded-lg p-3">{rating.comments}</p>
                </div>
              )}

              {/* Points forts et améliorations */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center space-x-2">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span>Points forts</span>
                  </h4>
                  <div className="space-y-1">
                    {rating.strengths.map((strength, idx) => (
                      <span
                        key={idx}
                        className="inline-block px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md mr-1 mb-1"
                      >
                        {strength}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center space-x-2">
                    <Target className="w-4 h-4 text-orange-600" />
                    <span>Axes d'amélioration</span>
                  </h4>
                  <div className="space-y-1">
                    {rating.improvements.map((improvement, idx) => (
                      <span
                        key={idx}
                        className="inline-block px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-md mr-1 mb-1"
                      >
                        {improvement}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Modal d'ajout (placeholder) */}
      {isAddModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Nouvelle évaluation</h3>
            <p className="text-gray-600 mb-4">
              Fonctionnalité en cours de développement...
            </p>
            <button
              onClick={() => setIsAddModalOpen(false)}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RatingsSection;
